/**
 * Behaviour that differs for interactive courses.
 *
 * `course.interactive` is derived server-side from the course having
 * Questions::Interactive questions (see Course#interactive?) — there is no
 * stored flag to set or forget. Three UI behaviours hang off it, all
 * consequences of the same fact: the challenges feed each other, the content is
 * the learner's own, and there is no prose to narrate.
 */

export const isInteractiveCourse = (enrolment: any): boolean =>
  !!enrolment?.course?.interactive;

export const isInteractiveQuestion = (question: any): boolean =>
  question?.kind === "Interactive";

/**
 * Interactive answers exist from the moment the lesson is opened — the server
 * seeds one so screens composed from earlier answers have something to render,
 * and so a shuffled picker does not reorder on every refresh.
 *
 * That makes `pending` mean something different for them than for every other
 * answer type. Elsewhere in combo `pending` means "submitted, awaiting
 * marking"; here it means "opened, still being worked on". Anything that reads
 * a pending answer as progress must ask this first.
 */
export const isUnstartedInteractiveAnswer = (
  question: any,
  answer: any,
): boolean => isInteractiveQuestion(question) && answer?.state === "pending";

/** Interactive courses cannot be personalised — the content is the learner's own work. */
export const allowsPersonalization = (enrolment: any): boolean =>
  !isInteractiveCourse(enrolment);

/** Interactive lessons have no narration, because there is no prose to read aloud. */
export const allowsLessonAudio = (enrolment: any): boolean =>
  !isInteractiveCourse(enrolment);

/**
 * Question ids the learner may open, in an interactive course.
 *
 * Everything already answered, plus the single next unanswered lesson — the
 * challenges depend on each other, so jumping ahead would land the learner on a
 * screen seeded from answers they have not given yet.
 *
 * `chapters` is the ordered topic list, each with an ordered `questions` array.
 */
export const unlockedQuestionIds = (
  chapters: any[],
  answers: any[],
  questionsById: Record<number, any> = {},
): Set<number> => {
  // A seeded-but-unsubmitted interactive answer does NOT count as done —
  // otherwise merely opening a lesson would unlock the next one, which defeats
  // the linear gate entirely.
  const answered = new Set(
    (answers || [])
      .filter((a) => !isUnstartedInteractiveAnswer(questionsById[a.question_id], a))
      .map((a) => a.question_id),
  );
  const unlocked = new Set<number>(answered);

  // Not flatMap: the project compiles against es6 lib.
  const ordered = (chapters || []).reduce(
    (acc: any[], chapter: any) => acc.concat(chapter?.questions || []),
    [],
  );
  const next = ordered.find((q) => !answered.has(q?.id));
  if (next) unlocked.add(next.id);

  return unlocked;
};

/**
 * Whether a lesson may be opened. Non-interactive courses are unrestricted, so
 * callers can apply this unconditionally.
 */
export const isLessonReachable = (
  enrolment: any,
  chapters: any[],
  answers: any[],
  questionId: number,
): boolean => {
  if (!isInteractiveCourse(enrolment)) return true;

  // The chapter lists carry each question, so the answer's own question can be
  // looked up to tell "seeded" from "genuinely answered".
  const questionsById: Record<number, any> = {};
  (chapters || []).forEach((chapter: any) =>
    (chapter?.questions || []).forEach((q: any) => {
      if (q?.id) questionsById[q.id] = q;
    }),
  );

  return unlockedQuestionIds(chapters, answers, questionsById).has(questionId);
};
